import React, { Component } from 'react';
import Helmet from 'react-helmet';

import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { compose } from 'recompose';

import { config, withServerData } from '@xingternal/hops';

import LoginKnownUser from './LoginKnownUser';
import LoginUser from './LoginUser';
import LoginOneTimeLinkConfirm from './LoginOneTimeLinkConfirm';

import Store, { storeToStateWhenReady } from '../../store';

import parseQueryString from '../../utils/parseQueryString';
import toQueryString from '../../utils/toQueryString';
import api from '../../utils/api';

import withActionTracker from '../../Components/PageviewTracker/withActionTracker';
import withPageviewTracker from '../../Components/PageviewTracker/withPageviewTracker';
import { toLoginPageviewTrackerContext } from '../../Components/PageviewTracker/toLoginPageviewTrackerContext';
import Overlay from '../../Components/Overlay/Overlay';

const initialValues = {
  password: '',
  username: '',
  permanent: true,
};

const getInitialFormValues = username =>
  Object.assign({}, initialValues, username ? { username } : {});

class LoginUserContainer extends Component {
  static contextType = Store;

  constructor(props) {
    super(props);

    const {
      serverData: {
        attemptedUsername,
        knownUser,
        authPartner,
        requestTime,
        formMessage,
        experiment,
      },
    } = props;

    this.state = {
      initialFormValues: getInitialFormValues(attemptedUsername),
      isLoading: false,
      attemptedUsername,
      knownUser,
      authPartner,
      requestTime,
      formMessage: formMessage || {},
      hasRequestedOneTimeLink: false,
      experiment,
    };

    this.usernameRef = React.createRef();
    this.passwordRef = React.createRef();

    this.forgotPassword = this.forgotPassword.bind(this);
    this.oldLogin = this.oldLogin.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.oneTimeLinkAction = this.oneTimeLinkAction.bind(this);
  }

  componentDidMount() {
    const { initialFormValues } = this.state;

    const usernameRef = this.usernameRef.current;
    const passwordRef = this.passwordRef.current;
    const domUsername = usernameRef && usernameRef.value;
    const domPassword = passwordRef && passwordRef.value;

    const stateWithStore = storeToStateWhenReady(
      this.state,
      this.context.state
    );

    this.setState(
      {
        ...stateWithStore,
        initialFormValues: {
          ...initialFormValues,
          username: stateWithStore.attemptedUsername || domUsername || '',
          password: domPassword || '',
        },
      },
      this.trackPageview
    );
  }

  trackPageview() {
    const mountTime = new Date().getTime();

    const {
      knownUser,
      didResetPassword,
      authPartner,
      requestTime,
      experiment,
    } = this.state;

    const timeToInteractive = requestTime && mountTime - requestTime;

    const pageviewTrackerContext = toLoginPageviewTrackerContext({
      hashedUserId: knownUser && knownUser.hashedId,
      authPartner,
      didResetPassword,
      timeToInteractive,
      experiment,
    });

    this.props.pageviewTrack('wbm/Welcome/login', pageviewTrackerContext);
  }

  forgotPassword(username) {
    const {
      history: { push },
    } = this.props;

    this.setState({
      isLoading: true,
    });

    api
      .post('/recovery/password/reset_requests')
      .then(() => {
        this.setState({
          isLoading: false,
        });

        this.context.dispatch({ type: 'SET_USERNAME', payload: { username } });
        push('/recovery/password/confirm');
      })
      .catch(err => {
        this.setState({
          isLoading: false,
        });

        console.error(err);
      });
  }

  oneTimeLinkAction() {
    const payload = parseQueryString(this.props.location.search);
    this.setState({
      isLoading: true,
    });

    api
      .post('/login/one_time_login_link', payload)
      .then(() => {
        this.setState({
          isLoading: false,
          oneTimeLinkError: null,
          hasRequestedOneTimeLink: true,
        });
      })
      .catch(e => {
        const errors = e.jsonResponse;

        this.setState({
          isLoading: false,
          oneTimeLinkError: errors.error,
        });
      });
  }

  oldLogin() {
    const queryObject = parseQueryString(this.props.location.search);
    const newQuery = toQueryString({
      not_me: true,
      sc_o: 'welcome_login_page_switch_user',
      ...queryObject,
    });

    window.location = '/continue?' + newQuery;
  }

  async handleSubmit(values, callbacks) {
    const { username, password, permanent } = values;
    const { setSubmitting, setErrors } = callbacks;
    const {
      location: { search },
    } = this.props;
    const { knownUser } = this.state;

    const queryObject = parseQueryString(search);
    const payload = {
      password,
      perm: permanent ? '1' : '0',
      ...queryObject,
    };

    let endpoint = '/login/promote';

    if (!knownUser) {
      endpoint = '/login';
      payload.username = username;
    }

    try {
      const { success, target } = await api.postRetry(endpoint, payload);

      if (success) {
        window.location = target;
      }
    } catch (err) {
      const { status } = err.response;
      const { description } = err.jsonResponse;

      if (status === 401) {
        this.props.track('welcome_login_error_wrong_credentials');
      } else {
        this.props.track('welcome_login_error_generic');
      }

      setErrors({ credentials: description });
      setSubmitting(false);
    }
  }

  render() {
    const {
      state: {
        isLoading,
        initialFormValues,
        knownUser,
        formMessage,
        authPartner,
        oneTimeLinkError,
        didResetPassword,
        hasRequestedOneTimeLink,
        experiment: { selectedVariantName },
      },
    } = this;

    return (
      <>
        <Helmet>
          <link rel={'canonical'} href={config.baseUrl} />
        </Helmet>
        <Formik
          enableReinitialize
          initialValues={initialFormValues}
          onSubmit={this.handleSubmit}
          render={formikProps =>
            knownUser ? (
              hasRequestedOneTimeLink ? (
                <LoginOneTimeLinkConfirm {...{ knownUser }} />
              ) : (
                <LoginKnownUser
                  {...this.props}
                  {...formikProps}
                  user={knownUser}
                  formMessage={formMessage}
                  passwordRef={this.passwordRef}
                  forgotPasswordAction={this.forgotPassword}
                  oldLoginAction={this.oldLogin}
                  oneTimeLinkAction={this.oneTimeLinkAction}
                  oneTimeLinkError={oneTimeLinkError}
                  didResetPassword={didResetPassword}
                  selectedVariantName={selectedVariantName}
                />
              )
            ) : (
              <LoginUser
                {...this.props}
                {...formikProps}
                usernameRef={this.usernameRef}
                passwordRef={this.passwordRef}
                authPartner={authPartner}
                formMessage={formMessage}
                didResetPassword={didResetPassword}
              />
            )
          }
        />
        <Overlay isVisible={isLoading} />
      </>
    );
  }
}

LoginUserContainer.propTypes = {
  track: PropTypes.func.isRequired,
  pageviewTrack: PropTypes.func.isRequired,
  location: PropTypes.object,
  serverData: PropTypes.shape({
    attemptedUsername: PropTypes.string,
    formMessage: PropTypes.object,
    authPartner: PropTypes.object,
    knownUser: PropTypes.object,
    requestTime: PropTypes.number,
    experiment: PropTypes.shape({
      name: PropTypes.string,
      variantsVector: PropTypes.arrayOf(PropTypes.string),
      selectedVariantName: PropTypes.string,
    }),
  }),
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }),
};

export default compose(
  withActionTracker,
  withPageviewTracker,
  withServerData
)(LoginUserContainer);
